/**
 * PX-6 — Notification centre derivations.
 *
 * Pure functions only. Notifications are durable rows owned by the signed-in
 * user (`notifications.user_id = auth.uid()`); read state lives in `read_at`,
 * so it survives devices and sessions. Nothing here invents a notification.
 */

export interface NotificationRow {
  id: string;
  title: string;
  message: string | null;
  channel: string | null;
  shipment_id: string | null;
  read_at: string | null;
  created_at: string;
}

export type NotificationCategory = "shipment" | "money" | "trust" | "system";

export interface DecoratedNotification extends NotificationRow {
  category: NotificationCategory;
  unread: boolean;
  /** In-app destination, or null when there is nothing specific to open. */
  to: string | null;
}

const MATCHERS: Array<{ category: NotificationCategory; re: RegExp }> = [
  { category: "money", re: /(payment|payout|invoice|refund|ledger|earning)/i },
  { category: "trust", re: /(verif|document|compliance|licence|license|expiry|expired|roadworthy|prdp)/i },
  { category: "shipment", re: /\b(shipment|load|trip|offer|pickup|delivery|pod|tracking|driver)\b/i },
];

export function categorise(n: NotificationRow): NotificationCategory {
  if (n.shipment_id) return "shipment";
  const hay = `${n.title} ${n.message ?? ""} ${n.channel ?? ""}`;
  for (const m of MATCHERS) if (m.re.test(hay)) return m.category;
  return "system";
}

export const CATEGORY_LABELS: Record<NotificationCategory, string> = {
  shipment: "Shipments",
  money: "Money",
  trust: "Trust & compliance",
  system: "System",
};

export function decorate(n: NotificationRow): DecoratedNotification {
  const category = categorise(n);
  return {
    ...n,
    category,
    unread: n.read_at === null,
    to: n.shipment_id ? `/app/shipper/shipments/${n.shipment_id}` : null,
  };
}

/** Newest first, unread ahead of read at equal recency. */
export function decorateAll(rows: NotificationRow[]): DecoratedNotification[] {
  return rows
    .map(decorate)
    .sort((a, b) => {
      if (a.unread !== b.unread) return a.unread ? -1 : 1;
      return a.created_at < b.created_at ? 1 : a.created_at > b.created_at ? -1 : 0;
    });
}

export const unreadCount = (rows: NotificationRow[]): number =>
  rows.reduce((n, r) => n + (r.read_at === null ? 1 : 0), 0);

/** Bell badges cap at 9+ so the shell never renders an unbounded number. */
export const badgeLabel = (count: number): string | null =>
  count <= 0 ? null : count > 9 ? "9+" : String(count);

export function groupByCategory(rows: DecoratedNotification[]): Array<{
  category: NotificationCategory;
  label: string;
  items: DecoratedNotification[];
}> {
  const order: NotificationCategory[] = ["shipment", "money", "trust", "system"];
  return order
    .map((category) => ({
      category,
      label: CATEGORY_LABELS[category],
      items: rows.filter((r) => r.category === category),
    }))
    .filter((g) => g.items.length > 0);
}

export function relativeTime(iso: string, now = Date.now()): string {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "";
  const mins = Math.floor((now - t) / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(t).toLocaleDateString();
}
